import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'query-string';
import { RootState } from 'modules/store';
import Errors from 'modules/shared/error/errors';
import type { PaginatedResponse } from 'modules/avs/avs.types';
import { downloadCSVFile } from 'utils/dowloadCSVFile';
import {
  FreelancerVerificationBulkDetails,
  FreelancerVerificationDetails,
  FreelancerVerificationRecord,
} from 'modules/freelancerVerification/freelancerVerification.types';
import FreelancerVerificationService from 'modules/freelancerVerification/freelancerVerification.service';

function getPage(): number {
  const queryParams = QueryString.parse(window.location.search);
  return Number(queryParams.page || 1);
}

interface RejectValue {
  message: string;
  status?: number;
  errorCode?: string;
}

export const uploadFreelancerBulkVerification = createAsyncThunk<
  FreelancerVerificationBulkDetails,
  {
    appToken: string;
    data: File;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'freelancerVerification/bulk-verification',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await FreelancerVerificationService.uploadBulkVerification({
        appToken,
        appEnvironment,
        data,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    } finally {
      const page = getPage();
      dispatch(getFreelancerVerificationRecords({ appToken, page }));
    }
  },
);

export const getFreelancerVerificationRecords = createAsyncThunk<
  PaginatedResponse<FreelancerVerificationRecord>,
  {
    appToken: string;
    page: number;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'freelancerVerification/get-verification-records',
  async ({ appToken, page }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await FreelancerVerificationService.getVerificationRecords({
        appToken,
        appEnvironment,
        page,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getFreelancerBulkVerificationResult = createAsyncThunk<
  FreelancerVerificationBulkDetails,
  {
    appToken: string;
    requestId: string;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'freelancerVerification/get-bulk-verification-result',
  async ({ appToken, requestId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await FreelancerVerificationService.getBulkVerificationResult({
        appToken,
        requestId,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getFreelancerBulkVerificationResultData = createAsyncThunk<
  PaginatedResponse<FreelancerVerificationDetails>,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    page?: number;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'freelancerVerification/get-bulk-verification-result-data',
  async (
    { appToken, requestId, isCsvFile, page },
    { getState, rejectWithValue },
  ) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await FreelancerVerificationService.getBulkVerificationResultData({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
        page,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `freelancer_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

